import { useEffect, useState, useMemo } from "react";
import { ReportBackButton } from "../ledgerReports/ReportBackButton";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { isUndefined, debounce } from "lodash";
import { useTheme, useMediaQuery } from "@mui/material";
import { globalStyle } from "../../global/Utils";
import { getStockReport } from "../../Actions/StockAction";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import StockMovementsModal from "./StockMovementsModal";
import { formatQty, stockChipStyle } from "../../global/stock";
import { SERIAL_LABEL } from "../../global/itemDisplay";

const StockChip = ({ state }) => {
  if (!state) return null;
  const style = stockChipStyle(state);
  return (
    <span
      style={{
        backgroundColor: style.bg,
        color: style.fg,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 8,
        whiteSpace: "nowrap",
      }}
    >
      {style.label}
    </span>
  );
};

/**
 * Item, quantity on hand and how healthy that is.
 *
 * Only stock-tracked products appear - the endpoint excludes service products
 * outright, because a stock row for something that carries no stock is noise.
 * Each row opens the movement history that produced its number.
 */
const StockReport = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [historyItem, setHistoryItem] = useState(null);

  const {
    stockReport,
    totalResults,
    isGettingStockReport,
    stockReportError,
    lowStockThreshold,
  } = useSelector((state) => state.StockReducer);

  useEffect(() => {
    dispatch(
      getStockReport(
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        stateFilter,
      ),
    );
  }, [pagination, dispatch, globalFilter, stateFilter]);

  const debouncedSetFilter = useMemo(
    () => debounce((value) => setGlobalFilter(value), 500),
    [],
  );

  useEffect(() => () => debouncedSetFilter.cancel(), [debouncedSetFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "name",
        header: "Item",
        size: 200,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "serial",
        header: SERIAL_LABEL,
        size: 130,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "companyName",
        header: "Company",
        size: 160,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "currentStock",
        header: "Current Quantity",
        size: 150,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => (
          <b>{formatQty(row.original?.currentStock, row.original?.unit)}</b>
        ),
      },
      {
        accessorKey: "stockState",
        header: "Status",
        size: 130,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => <StockChip state={row.original?.stockState} />,
      },
      {
        id: "actions",
        header: "History",
        size: 90,
        grow: 0,
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="Movement history">
            <IconButton
              size="small"
              onClick={() => setHistoryItem(row.original)}
              sx={{ color: "black" }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line
    [pagination],
  );

  const renderMobileCards = () =>
    stockReport.map((row, index) => (
      <Box
        key={row._id}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box sx={{ fontSize: 12, color: "#999" }}>
            #{index + 1 + pagination.pageIndex * pagination.pageSize}
          </Box>
          <Tooltip title="Movement history">
            <IconButton
              size="small"
              onClick={() => setHistoryItem(row)}
              sx={{ color: "black" }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ mb: 1 }}>
          <b>Item:</b>
          <Box sx={{ fontSize: 14 }}>{row.name || "-"}</Box>
        </Box>
        <Box sx={{ mb: 1 }}>
          <b>Company:</b>
          <Box sx={{ fontSize: 14 }}>{row.companyName || "-"}</Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <b>{formatQty(row.currentStock, row.unit)}</b>
          <StockChip state={row.stockState} />
        </Box>
      </Box>
    ));

  return (
    <Box className="p-4">
      <ReportBackButton />
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall,
                sm: globalStyle.headingFontSize,
              },
              lineHeight: 1.2,
            }}
          >
            Stock Report
          </Box>
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: { xs: 12, sm: 14 },
              color: "#666",
            }}
          >
            Quantities on hand, derived from the stock ledger. Low at{" "}
            {lowStockThreshold} or below.
          </Box>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={stateFilter}
          onChange={(_, value) => {
            setStateFilter(value ?? "");
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="LOW">Low</ToggleButton>
          <ToggleButton value="OUT">Out</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {stockReportError ? (
        <Box py={5} textAlign="center" color="error.main">
          {stockReportError}
        </Box>
      ) : isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingStockReport}
          searchLabel="Stock"
        >
          {renderMobileCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={stockReport}
          enableRowActions={false}
          manualPagination
          rowCount={totalResults}
          state={{
            columnOrder: [
              "_id",
              "name",
              "serial",
              "companyName",
              "currentStock",
              "stockState",
              "actions",
            ],
            columnPinning: { right: ["actions"] },
            isLoading: isGettingStockReport,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": { backgroundColor: globalStyle.bgColor },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none",
              overflow: "visible",
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{ sx: { tableLayout: "fixed" } }}
        />
      )}

      <StockMovementsModal
        open={historyItem !== null}
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
    </Box>
  );
};

export default StockReport;
